import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Settings, AlertTriangle, CheckCircle, Clock } from 'lucide-react';

export const ProductionDashboard = () => {
  const { data: productionRuns = [], isLoading } = useQuery({
    queryKey: ['production-dashboard'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('production_runs')
        .select(`
          *,
          finished_product:finished_product_id(name, sku)
        `)
        .order('created_at', { ascending: false })
        .limit(10);
      
      if (error) throw error;
      return data;
    },
  });

  const { data: lowStockItems = [] } = useQuery({
    queryKey: ['low-stock-items'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('inventory_items')
        .select('name, sku, current_stock, min_stock_level, type')
        .lte('current_stock', 10)
        .order('current_stock');
      
      if (error) throw error;
      return data.filter(item => item.current_stock <= (item.min_stock_level || 0));
    },
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'in_progress':
        return <Clock className="h-4 w-4 text-blue-500" />;
      case 'planned':
        return <Clock className="h-4 w-4 text-gray-500" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
    }
  };

  const getProgressPercentage = (produced: number, planned: number) => {
    return Math.min((produced / planned) * 100, 100);
  };

  return (
    <div className="grid gap-4 sm:gap-6 grid-cols-1 lg:grid-cols-2">
      <Card className="mobile-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Settings className="h-4 w-4 sm:h-5 sm:w-5" />
            Recent Production Runs
          </CardTitle>
          <CardDescription className="text-sm">
            Latest manufacturing activities
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="table-scroll-container">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="table-text-mobile">Product</TableHead>
                  <TableHead className="table-text-mobile">Progress</TableHead>
                  <TableHead className="table-text-mobile">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center table-text-mobile">Loading...</TableCell>
                  </TableRow>
                ) : productionRuns.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center table-text-mobile">No production runs</TableCell>
                  </TableRow>
                ) : (
                  productionRuns.map((run) => (
                    <TableRow key={run.id}>
                      <TableCell className="table-text-mobile">
                        <div>
                          <div className="font-medium">
                            {(run.finished_product as any)?.name}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {(run.finished_product as any)?.sku}
                          </div>
                        </div>
                      </TableCell>
                    <TableCell className="table-text-mobile">
                      <div className="space-y-2">
                        <div className="flex justify-between text-xs sm:text-sm">
                          <span>{run.quantity_produced}/{run.quantity_to_produce}</span>
                          <span>{getProgressPercentage(run.quantity_produced, run.quantity_to_produce).toFixed(0)}%</span>
                        </div>
                        <Progress 
                          value={getProgressPercentage(run.quantity_produced, run.quantity_to_produce)} 
                          className="h-2"
                        />
                      </div>
                    </TableCell>
                    <TableCell className="table-text-mobile">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(run.status)}
                        <Badge variant={run.status === 'completed' ? 'default' : 'secondary'} className="text-xs">
                          {run.status?.toUpperCase() || 'UNKNOWN'}
                        </Badge>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>

      <Card className="mobile-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-orange-500" />
            Low Stock Alerts
          </CardTitle>
          <CardDescription className="text-sm">
            Items requiring immediate attention
          </CardDescription>
        </CardHeader>
        <CardContent>
          {lowStockItems.length === 0 ? (
            <div className="text-center py-6">
              <CheckCircle className="h-6 w-6 sm:h-8 sm:w-8 text-green-500 mx-auto mb-2" />
              <p className="text-xs sm:text-sm text-muted-foreground">All items are well stocked</p>
            </div>
          ) : (
            <div className="space-y-2 sm:space-y-3">
              {lowStockItems.slice(0, 5).map((item) => (
                <div key={item.sku} className="flex items-center justify-between p-2 sm:p-3 border rounded-lg">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-xs sm:text-sm truncate">{item.name}</div>
                    <div className="text-xs text-muted-foreground">{item.sku}</div>
                  </div>
                  <div className="text-right shrink-0 ml-2">
                    <div className="font-medium text-red-600 text-xs sm:text-sm">
                      {item.current_stock} units
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Min: {item.min_stock_level || 0}
                    </div>
                  </div>
                </div>
              ))}
              {lowStockItems.length > 5 && (
                <div className="text-center pt-2">
                  <Badge variant="outline" className="text-xs">
                    +{lowStockItems.length - 5} more items
                  </Badge>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};